import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CalendarCheck, Car, CheckCircle, Clock } from '@phosphor-icons/react';
import API_URL from '@/config';
import { getClientFacingDriverName } from '../../utils/driverDisplay';
import { COURSE_STATUS_LABELS, COURSE_STATUS_STYLES, isStatusAtOrAfter, normalizeCourseStatus, statusEquals } from '../../utils/courseWorkflow';

const ClientDashboard = () => {
  const { lang } = useParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/bookings/my`, { withCredentials: true });
        setBookings(response.data);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const stats = {
    total: bookings.length,
    pending: bookings.filter((b) => statusEquals(b.status, 'DRAFT') || statusEquals(b.status, 'QUOTE_SENT')).length,
    assigned: bookings.filter((b) => isStatusAtOrAfter(b.status, 'ASSIGNED') && !isStatusAtOrAfter(b.status, 'COMPLETED')).length,
    completed: bookings.filter((b) => isStatusAtOrAfter(b.status, 'COMPLETED')).length,
  };

  const recentBookings = bookings.slice(0, 3);

  const getStatusBadge = (status) => {
    const normalized = normalizeCourseStatus(status);
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${COURSE_STATUS_STYLES[normalized] || COURSE_STATUS_STYLES.DRAFT}`}>
        {COURSE_STATUS_LABELS[normalized] || normalized}
      </span>
    );
  };

  return (
    <div className="bg-[#0A0A0A] text-white min-h-full">
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4" data-testid="client-stats">
        <Link to={`/${lang}/client/bookings`} className="glass block rounded-xl p-5 transition-all cursor-pointer hover:border-[#D4AF37]/50 sm:p-6">
          <CalendarCheck size={32} className="text-[#D4AF37] mb-3" />
          <p className="text-3xl font-bold">{stats.total}</p>
          <p className="text-[#A1A1AA] text-sm">Total courses</p>
        </Link>
        <Link to={`/${lang}/client/bookings?status=DRAFT`} className="glass block rounded-xl p-5 transition-all cursor-pointer hover:border-[#D4AF37]/50 sm:p-6">
          <Clock size={32} className="text-yellow-400 mb-3" />
          <p className="text-3xl font-bold">{stats.pending}</p>
          <p className="text-[#A1A1AA] text-sm">En attente</p>
        </Link>
        <Link to={`/${lang}/client/bookings?status=IN_PROGRESS`} className="glass block rounded-xl p-5 transition-all cursor-pointer hover:border-[#D4AF37]/50 sm:p-6">
          <Car size={32} className="text-blue-400 mb-3" />
          <p className="text-3xl font-bold">{stats.assigned}</p>
          <p className="text-[#A1A1AA] text-sm">En cours</p>
        </Link>
        <Link to={`/${lang}/client/bookings?status=COMPLETED`} className="glass block rounded-xl p-5 transition-all cursor-pointer hover:border-[#D4AF37]/50 sm:p-6">
          <CheckCircle size={32} className="text-green-400 mb-3" />
          <p className="text-3xl font-bold">{stats.completed}</p>
          <p className="text-[#A1A1AA] text-sm">Terminées</p>
        </Link>
      </div>

      <div className="mb-8">
        <Button asChild className="bg-[#D4AF37] hover:bg-[#F0C74A] text-[#0A0A0A] font-semibold">
          <Link to={`/${lang}/client/new-booking`} data-testid="new-booking-btn">
            <Car size={20} className="mr-2" />
            Nouvelle réservation
          </Link>
        </Button>
      </div>

      <div className="glass rounded-xl p-6" data-testid="recent-bookings">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold font-['Cormorant_Garamond']">Dernières réservations</h2>
          <Link to={`/${lang}/client/bookings`} className="text-[#D4AF37] hover:underline text-sm">Voir tout</Link>
        </div>

        {loading ? (
          <div className="text-center py-8 text-[#A1A1AA]">Chargement...</div>
        ) : recentBookings.length === 0 ? (
          <div className="text-center py-8 text-[#A1A1AA]">Aucune réservation</div>
        ) : (
          <div className="space-y-4">
            {recentBookings.map((booking) => (
              <Link key={booking.id} to={`/${lang}/client/bookings/${booking.id}`} className="block bg-[#1E1E1E] rounded-lg p-4 border border-white/5 hover:bg-[#252525] transition-colors">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium">{booking.pickup_date} à {booking.pickup_time}</p>
                    <p className="text-sm text-[#A1A1AA]">{booking.pickup_address}</p>
                    <p className="text-sm text-[#A1A1AA]">→ {booking.dropoff_address}</p>
                  </div>
                  {getStatusBadge(booking.status)}
                </div>
                <p className="text-sm text-[#D4AF37] mt-2">Chauffeur: {getClientFacingDriverName(booking)}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDashboard;
